<script setup lang="ts">
/**
 * Keeps a list of words or terms — a spell-check dictionary, stop words,
 * keywords — built for hundreds or thousands of entries: a field that adds a
 * word (Enter or „Hinzufügen“), a search with its count („10 von 301
 * Wörtern“), the list sorted for the reader's locale and shown a page at a
 * time („Weitere Wörter anzeigen“, also on scroll), one remove action per row,
 * and import/export buttons.
 *
 * The component decides nothing about the data. `add` hands over the trimmed
 * word and the host checks it (too long, already there): it clears the field
 * through `v-model:draft` when it takes the word, or says why not in `hint`.
 * `remove` hands over the word. Import picks ONE file (`pickFiles`, `accept`)
 * and hands it to `@import`; parsing it, like writing the `@export` file, is
 * the host's. The buttons only show when the host binds `@import`/`@export`.
 * `messages` is the host's status area above the field — what an import did,
 * a failed save, a lock.
 *
 * `addMode="button"` (opt-in) moves the add field out of sight: the tools row
 * gets a „Wort hinzufügen“ button next to import/export, and a click opens ONE
 * input line under it (Enter or „Hinzufügen“ adds and keeps the line open for
 * the next word, Escape or „Abbrechen“ closes it). The default `'field'` keeps
 * the field above the tools.
 *
 * Words that come from elsewhere carry a `badge` (their origin) and offer no
 * removal unless `removable` says so. `readonly` shows the whole list without
 * the field, the removal and the import.
 */
import { computed, inject, nextTick, ref, useTemplateRef, watch } from 'vue';
import Download from 'lucide-vue-next/dist/esm/icons/download.js'; import Upload from 'lucide-vue-next/dist/esm/icons/upload.js'; import X from 'lucide-vue-next/dist/esm/icons/x.js';
import { NxBadge } from '../Badge';
import { NxBanner, type NxBannerVariant } from '../Banner';
import { NxButton } from '../Button';
import { NxEmptyState } from '../EmptyState';
import { pickFiles } from '../Files';
import { NxIconButton } from '../IconButton';
import { NxSearchInput, NxTextInput } from '../Inputs';
import { NxList, NxListRow } from '../ListRow';
import { NxLoadMore } from '../LoadMore';
import { i18nInjectionKey } from '../internal';

/** One word of the list. */
export interface NxWordListItem {
  word: string;
  /** Where the word comes from („Von der Firma“), shown as a badge. */
  badge?: string;
  /** Tooltip of the badge (why the word cannot be removed here). */
  badgeHint?: string;
  /** Whether the row offers removal. Default: only words without a badge. */
  removable?: boolean;
}

/** A status line of the host above the field. */
export interface NxWordListMessage {
  /** Stable key; also on the banner as `data-message`. */
  id: string;
  variant: NxBannerVariant;
  text: string;
}

const props = withDefaults(
  defineProps<{
    items: readonly NxWordListItem[];
    /** Accessible name of the list. */
    label?: string;
    /** Shows the list only: no field, no removal, import disabled. */
    readonly?: boolean;
    /** Why the typed word was not added (too long, already there); cleared by the host. */
    hint?: string | null;
    /** The host's status lines (import result, save error, lock). */
    messages?: readonly NxWordListMessage[];
    /** Rows shown at first and added per „Weitere laden“. */
    pageSize?: number;
    /** File types the import offers (`<input accept>`). */
    accept?: string | readonly string[];
    /** Sorting, search and numbers; default the app's locale. */
    locale?: string;
    /**
     * How a word is added: `'field'` (default) shows the input above the
     * tools; `'button'` shows a „Wort hinzufügen“ button in the tools row that
     * opens the input line under it.
     */
    addMode?: 'field' | 'button';
    /** Called with the picked file (`@import`); without it there is no import button. */
    onImport?: (file: File) => unknown;
    /** Called to export the list (`@export`); without it there is no export button. */
    onExport?: () => unknown;
  }>(),
  {
    label: undefined,
    readonly: false,
    hint: null,
    messages: () => [],
    pageSize: 100,
    accept: '.txt,.dic,text/plain',
    locale: undefined,
    addMode: 'field',
    onImport: undefined,
    onExport: undefined,
  }
);

const emit = defineEmits<{
  /** The trimmed, non-empty word typed in. */
  add: [word: string];
  remove: [word: string];
}>();

/** The typed word; the host clears it when it takes the word. */
const draft = defineModel<string>('draft', { default: '' });

const i18n = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale ?? i18n?.locale?.value ?? 'de');

function text(key: string, params?: Record<string, unknown>): string {
  const id = `common.word_list.${key}`;
  if (!i18n) return id;
  return params ? i18n.t(id, params) : i18n.t(id);
}

function formatCount(count: number): string {
  return new Intl.NumberFormat(locale.value).format(count);
}

/* ── Sorting, search and paging ── */

const sorted = computed<NxWordListItem[]>(() => {
  const collator = new Intl.Collator(locale.value);
  return [...props.items].sort((a, b) => collator.compare(a.word, b.word));
});

const query = ref('');
const needle = computed(() => query.value.trim().toLocaleLowerCase(locale.value));
const shown = ref(props.pageSize);
watch(query, () => {
  shown.value = props.pageSize;
});

const filtered = computed<NxWordListItem[]>(() => {
  if (!needle.value) return sorted.value;
  return sorted.value.filter((item) =>
    item.word.toLocaleLowerCase(locale.value).includes(needle.value)
  );
});
const visible = computed(() => filtered.value.slice(0, shown.value));

const countText = computed(() => {
  const total = props.items.length;
  const count = formatCount(total);
  if (!needle.value) {
    if (total === 0) return text('count_none');
    return text(total === 1 ? 'count_one' : 'count_many', { count });
  }
  const found = formatCount(filtered.value.length);
  return text(total === 1 ? 'count_filtered_one' : 'count_filtered_many', { found, count });
});

function removable(item: NxWordListItem): boolean {
  return !props.readonly && (item.removable ?? !item.badge);
}

/* ── Adding and removing ── */

function add(): void {
  if (props.readonly) return;
  const word = draft.value.trim();
  if (!word) return;
  emit('add', word);
  // Button mode: the line stays open for the next word, the focus stays in it.
  if (props.addMode === 'button') focusAddInput();
}

/* ── The add line of `addMode="button"` ── */

const adding = ref(false);
const addInput = useTemplateRef<{ inputElement: HTMLInputElement | null }>('add-input');
/** The line of button mode; the field of `'field'` mode is always there. */
const addLineOpen = computed(() => props.addMode === 'button' && adding.value && !props.readonly);

function focusAddInput(): void {
  void nextTick(() => addInput.value?.inputElement?.focus());
}

function openAddLine(): void {
  adding.value = true;
  focusAddInput();
}

const openButton = useTemplateRef<{ $el: HTMLElement }>('open-add');

/** Closes the line, drops the typing and hands the focus back to the button that opened it. */
function closeAddLine(): void {
  adding.value = false;
  draft.value = '';
  void nextTick(() => openButton.value?.$el?.focus());
}

const listBox = useTemplateRef<HTMLElement>('list-box');
const searchInput = useTemplateRef<{ inputElement: HTMLInputElement | null }>('search');
/** The row whose remove button had focus, to hand focus on once it is gone. */
let removed: { word: string; index: number } | null = null;

function remove(word: string, index: number): void {
  removed = { word, index };
  emit('remove', word);
}

// Keyboard users keep their place: the row's button disappears with the row,
// so the next row's remove button (or the search) takes the focus.
watch(
  visible,
  async (rows) => {
    const last = removed;
    if (!last) return;
    removed = null;
    if (rows.some((item) => item.word === last.word)) return;
    await nextTick();
    // Only when the focus went down with the row, never away from where the user moved it.
    const active = document.activeElement;
    if (active && active !== document.body) return;
    const buttons = listBox.value?.querySelectorAll<HTMLElement>('[data-word-remove]') ?? [];
    const target = buttons[Math.min(last.index, buttons.length - 1)];
    (target ?? searchInput.value?.inputElement)?.focus();
  },
  { flush: 'post' }
);

/* ── Import and export ── */

async function importFile(): Promise<void> {
  if (props.readonly || !props.onImport) return;
  const [file] = await pickFiles({ accept: props.accept });
  if (file) await props.onImport(file);
}

function exportFile(): void {
  void props.onExport?.();
}
</script>

<template>
  <div class="nx-word-list-editor" data-testid="nx-word-list-editor">
    <div v-if="messages.length" class="nx-word-list-editor__messages" role="status">
      <NxBanner
        v-for="message in messages"
        :key="message.id"
        :variant="message.variant"
        :data-message="message.id"
        data-testid="nx-word-list-message"
      >
        {{ message.text }}
      </NxBanner>
    </div>

    <div v-if="!readonly && addMode === 'field'" class="nx-word-list-editor__add">
      <NxTextInput
        v-model="draft"
        :aria-label="text('add_label')"
        :placeholder="text('placeholder')"
        autocomplete="off"
        spellcheck="false"
        data-testid="nx-word-list-add-input"
        @keydown.enter.prevent="add"
      />
      <NxButton :disabled="!draft.trim()" data-testid="nx-word-list-add" @click="add">
        {{ text('add') }}
      </NxButton>
    </div>
    <p
      v-if="hint && addMode === 'field'"
      class="nx-word-list-editor__hint"
      role="status"
      data-testid="nx-word-list-hint"
    >
      {{ hint }}
    </p>

    <div class="nx-word-list-editor__tools">
      <div class="nx-word-list-editor__search">
        <NxSearchInput
          ref="search"
          v-model="query"
          :aria-label="text('search')"
          data-testid="nx-word-list-search"
        />
        <span
          class="nx-word-list-editor__count"
          aria-live="polite"
          data-testid="nx-word-list-count"
        >
          {{ countText }}
        </span>
      </div>
      <div
        v-if="(addMode === 'button' && !readonly) || onImport || onExport"
        class="nx-word-list-editor__transfer"
      >
        <NxButton
          v-if="addMode === 'button' && !readonly"
          ref="open-add"
          :aria-expanded="addLineOpen"
          data-testid="nx-word-list-open-add"
          @click="openAddLine"
        >
          {{ text('add_open') }}
        </NxButton>
        <NxButton
          v-if="onImport"
          variant="outline"
          :disabled="readonly"
          data-testid="nx-word-list-import"
          @click="importFile"
        >
          <Upload class="nxt-icon-sm" aria-hidden="true" />
          {{ text('import') }}
        </NxButton>
        <NxButton
          v-if="onExport"
          variant="outline"
          :disabled="!items.length"
          data-testid="nx-word-list-export"
          @click="exportFile"
        >
          <Download class="nxt-icon-sm" aria-hidden="true" />
          {{ text('export') }}
        </NxButton>
      </div>
    </div>

    <div v-if="addLineOpen" class="nx-word-list-editor__add">
      <NxTextInput
        ref="add-input"
        v-model="draft"
        :escape-restores="false"
        :aria-label="text('add_label')"
        :placeholder="text('placeholder')"
        autocomplete="off"
        spellcheck="false"
        data-testid="nx-word-list-add-input"
        @keydown.enter.prevent="add"
        @keydown.esc.prevent.stop="closeAddLine"
      />
      <NxButton :disabled="!draft.trim()" data-testid="nx-word-list-add" @click="add">
        {{ text('add') }}
      </NxButton>
      <NxButton variant="ghost" data-testid="nx-word-list-cancel" @click="closeAddLine">
        {{ text('cancel') }}
      </NxButton>
    </div>
    <p
      v-if="hint && addMode === 'button'"
      class="nx-word-list-editor__hint"
      role="status"
      data-testid="nx-word-list-hint"
    >
      {{ hint }}
    </p>

    <div v-if="visible.length" ref="list-box">
      <NxList divided :aria-label="label" data-testid="nx-word-list">
        <NxListRow
          v-for="(item, index) in visible"
          :key="item.word"
          :title="item.word"
          :clickable="false"
          dense
        >
          <template v-if="item.badge" #trailing>
            <NxBadge variant="neutral" :title="item.badgeHint">{{ item.badge }}</NxBadge>
          </template>
          <template v-if="removable(item)" #actions>
            <NxIconButton
              :title="text('remove', { word: item.word })"
              data-word-remove
              data-testid="nx-word-list-remove"
              @click="remove(item.word, index)"
            >
              <X class="nxt-icon-sm" aria-hidden="true" />
            </NxIconButton>
          </template>
        </NxListRow>
      </NxList>
      <NxLoadMore
        v-if="filtered.length > visible.length"
        auto
        :loaded="visible.length"
        :total="filtered.length"
        :show-end="false"
        :label="text('load_more')"
        :count-label="
          text('shown', { shown: formatCount(visible.length), count: formatCount(filtered.length) })
        "
        :locale="locale"
        @load="shown += pageSize"
      />
    </div>
    <NxEmptyState v-else compact :title="needle ? text('no_match') : text('empty')" />
  </div>
</template>

<style scoped>
.nx-word-list-editor,
.nx-word-list-editor__messages {
  display: grid;
  gap: var(--nxt-space-sm);
  min-width: 0;
}

.nx-word-list-editor__add,
.nx-word-list-editor__search {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-width: 0;
}

.nx-word-list-editor__add > :first-child,
.nx-word-list-editor__search > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}

/* Search and count on the left, import/export on the right; on a narrow
   card the buttons move below the search instead of squeezing it. */
.nx-word-list-editor__tools {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs) var(--nxt-space-sm);
}

.nx-word-list-editor__search {
  flex: 1 1 calc(var(--nxt-size-unit) * 16);
}

.nx-word-list-editor__transfer {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: var(--nxt-space-xs);
}

.nx-word-list-editor__count {
  flex: none;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}

.nx-word-list-editor__hint {
  margin: 0;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-sm);
}
</style>
